"use client";

import { Fragment, type ReactNode, createContext, useContext, useEffect, useState } from "react";

import {
  type AuthError,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getRedirectResult,
  linkWithCredential, // linkWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
} from "firebase/auth";
import { v4 as uuidv4 } from "uuid";

import {
  BROWSER_SESSION_CLEARED,
  BROWSER_SESSION_UNAVAILABLE,
  failBrowserSession,
  assertCurrentBrowserSession,
  isBrowserSessionClearing,
  prepareBrowserSession,
  watchBrowserRevocation,
} from "@/lib/browser-session";
import { auth } from "@/lib/firebase-client";
import { buildPathWithQuery, consumePendingAuthRedirect } from "@/lib/pending-auth-redirect";
import { createUser, getUser, updateUser } from "@/lib/shop-agent-client";
import { isPopupClosedAuthError, toFriendlyAuthError } from "@/utils/auth-error-helpers";

// Return type for auth methods indicating if this was a new user registration
interface AuthResult {
  isNewUser: boolean;
}

type OAuthAuthResult =
  | { status: "success"; isNewUser: boolean }
  | { status: "redirect" }
  | { status: "cancelled" };

// Define the authentication context types
interface AuthContextType {
  user: User | null;
  userId: string | null;
  isAuthenticated: boolean;
  isAnonymous: boolean;
  isOnboarded: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  // Returns { isNewUser: true } for new registrations, { isNewUser: false } if email was already in use
  register: (email: string, password: string, displayName: string) => Promise<AuthResult>;
  // OAuth methods return explicit outcomes for success, redirect, and user-cancelled popups.
  loginWithGoogle: () => Promise<OAuthAuthResult>;
  loginWithApple: () => Promise<OAuthAuthResult>;
  logout: () => Promise<void>;
  updateUserProfile: (displayName: string, photoURL?: string) => Promise<void>;
  setOnboardingComplete: () => Promise<void>;
  refreshOnboardingStatus: () => Promise<void>;
  // Legacy compatibility
  checkAuthStatus: () => Promise<void>;
  ensureUserId: () => string;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Production domains where redirect auth works reliably
// These must exactly match domains configured in Google OAuth and Firebase Auth settings
// Preview URLs (like shop-agent-xxx.octogen.app) will NOT match and will use popup instead
const PRODUCTION_DOMAINS = ["cosimo.shop"];

// Helper to check if we're on a production domain where redirect auth works
// Only exact matches - subdomains like shop-agent-xxx.octogen.app should use popup
const isProductionDomain = (): boolean => {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  return PRODUCTION_DOMAINS.includes(hostname);
};

// Hook to use the authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [sessionCleared, setSessionCleared] = useState(false);
  const [sessionUnavailable, setSessionUnavailable] = useState(false);

  useEffect(() => {
    const clear = () => {
      setSessionCleared(true);
      setUser(null);
      setIsOnboarded(false);
    };
    const fail = () => {
      setSessionCleared(false);
      setSessionUnavailable(true);
      setUser(null);
      setIsOnboarded(false);
      setLoading(false);
    };
    window.addEventListener(BROWSER_SESSION_CLEARED, clear);
    window.addEventListener(BROWSER_SESSION_UNAVAILABLE, fail);
    const stop = watchBrowserRevocation();
    return () => {
      stop();
      window.removeEventListener(BROWSER_SESSION_CLEARED, clear);
      window.removeEventListener(BROWSER_SESSION_UNAVAILABLE, fail);
    };
  }, []);

  const navigateToPendingAuthRedirect = (): boolean => {
    if (typeof window === "undefined") return false;

    const pendingPath = consumePendingAuthRedirect();
    if (!pendingPath) return false;

    const currentPath = buildPathWithQuery(window.location.pathname, window.location.search);
    if (currentPath === pendingPath) {
      return false;
    }

    window.location.replace(pendingPath);
    return true;
  };

  // Function to check user status from API
  const checkUserStatus = async (firebaseUser: User): Promise<boolean> => {
    try {
      // Try to get existing user
      let userData;
      try {
        userData = await getUser();
      } catch (error) {
        if (
          (error as { status?: number }).status !== 404 ||
          auth?.currentUser?.uid !== firebaseUser.uid ||
          isBrowserSessionClearing()
        )
          throw error;
        // Only a verified missing profile can create one.
        userData = await createUser({
          display_name: firebaseUser.displayName,
          email: firebaseUser.email,
        });
      }

      return userData.is_onboarded || false;
    } catch (error) {
      console.error("Error checking user status:", error);
      return false;
    }
  };

  useEffect(() => {
    if (!auth) {
      // In CI smoke tests (NEXT_PUBLIC_CYPRESS=true, no Firebase keys),
      // unblock the loading gate so the app renders and Cypress intercepts work.
      // In production we keep loading=true to avoid flashing unauthed UI.
      if (process.env.NEXT_PUBLIC_CYPRESS === "true") {
        setLoading(false);
      }
      return () => {};
    }

    // Capture auth in a const so TypeScript knows it's defined in the async functions
    const firebaseAuth = auth;
    let unsubscribe: (() => void) | undefined;
    // Track if we got a user from redirect - only skip ONE anonymous sign-in attempt
    let didGetRedirectUser = false;
    // Cancellation flag for cleanup race condition
    let isMounted = true;

    const initialize = async () => {
      // On production domains, check for redirect result first
      // This handles the case where user returns from OAuth redirect
      if (isProductionDomain()) {
        try {
          console.debug("[AuthContext] Production domain, checking for redirect result...");
          const result = await getRedirectResult(firebaseAuth);
          if (!isMounted) return; // Component unmounted during async operation
          const redirectReady = result ? await prepareBrowserSession(result.user.uid) : false;
          if (!isMounted) return;
          if (result && redirectReady) {
            console.debug("[AuthContext] ✅ Redirect result received:", result.user.email);
            didGetRedirectUser = true;
            setUser(result.user);
            setLoading(false);

            // Check if this was a new user signup from OAuth redirect
            checkUserStatus(result.user)
              .then((onboarded) => {
                if (auth?.currentUser?.uid === result.user.uid && !isBrowserSessionClearing())
                  setIsOnboarded(onboarded);
              })
              .catch(() => setIsOnboarded(false));

            if (navigateToPendingAuthRedirect()) {
              return;
            }
          } else {
            didGetRedirectUser = false;
          }
        } catch (error) {
          console.error("[AuthContext] Redirect result error:", error);
        }
      }

      if (!isMounted) return; // Component unmounted during async operation

      // Set up auth state listener
      unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
        if (!isMounted || isBrowserSessionClearing()) return; // Component unmounted/revoked

        console.debug("[AuthContext] onAuthStateChanged fired:", {
          user: firebaseUser?.uid,
          isAnonymous: firebaseUser?.isAnonymous,
          email: firebaseUser?.email,
          didGetRedirectUser,
        });

        if (!firebaseUser) {
          // Skip anonymous sign-in only once if we just got a user from redirect
          // This prevents a race where onAuthStateChanged fires with null before
          // the redirect user is fully processed
          if (didGetRedirectUser) {
            console.debug("[AuthContext] Skipping anonymous sign-in, processing redirect user");
            // Reset flag so future logouts will trigger anonymous sign-in
            didGetRedirectUser = false;
            return;
          }

          // If no user is logged in, sign in anonymously for API access
          console.debug("[AuthContext] No user, signing in anonymously...");
          try {
            await signInAnonymously(firebaseAuth);
            console.debug("[AuthContext] Anonymous sign-in initiated");
          } catch (error) {
            console.error("[AuthContext] Failed to sign in anonymously:", error);
            setUser(null);
            setLoading(false);
            setIsOnboarded(false);
          }
        } else {
          // User is present - reset the redirect flag since we've processed them
          didGetRedirectUser = false;

          console.debug("[AuthContext] User present:", {
            email: firebaseUser.email,
            isAnonymous: firebaseUser.isAnonymous,
          });
          try {
            if (!(await prepareBrowserSession(firebaseUser.uid)) || !isMounted) return;
          } catch {
            failBrowserSession();
            return;
          }
          setUser(firebaseUser);
          setLoading(false);
          if (!firebaseUser.isAnonymous) {
            console.debug("[AuthContext] Real user, checking onboarding status");
            checkUserStatus(firebaseUser)
              .then((onboarded) => {
                console.debug("[AuthContext] Onboarding status:", onboarded);
                if (auth?.currentUser?.uid === firebaseUser.uid && !isBrowserSessionClearing())
                  setIsOnboarded(onboarded);
              })
              .catch(() => setIsOnboarded(false));
          } else {
            console.debug("[AuthContext] Firebase anonymous user, setting onboarded = false");
            setIsOnboarded(false);
          }
        }
      });
    };

    initialize();

    return () => {
      isMounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    assertCurrentBrowserSession(undefined);
    if (!auth) throw new Error("Firebase auth not initialized");
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      // Update onboarding status without blocking
      checkUserStatus(userCredential.user)
        .then((onboarded) => {
          if (auth?.currentUser?.uid === userCredential.user.uid && !isBrowserSessionClearing())
            setIsOnboarded(onboarded);
        })
        .catch(() => setIsOnboarded(false));
    } catch (error) {
      throw toFriendlyAuthError(error, {
        fallback: "We couldn't sign you in. Please try again.",
        flow: "sign-in",
      });
    }
  };

  const resetPassword = async (email: string) => {
    if (!auth) throw new Error("Firebase auth not initialized");

    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      // Firebase projects with email enumeration protection disabled can still surface
      // auth/user-not-found here. Treat it as success so the UI never reveals whether
      // an account exists for the submitted email address.
      if ((error as AuthError).code === "auth/user-not-found") {
        return;
      }

      throw toFriendlyAuthError(error, {
        fallback: "We couldn't send a reset email. Please try again.",
        flow: "sign-in",
      });
    }
  };

  const register = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<AuthResult> => {
    assertCurrentBrowserSession(undefined);
    const currentUser = auth?.currentUser;

    // If user is anonymous, try to link the credentials to convert to permanent account
    if (currentUser && currentUser.isAnonymous) {
      try {
        const credential = EmailAuthProvider.credential(email, password);
        const userCredential = await linkWithCredential(currentUser, credential);
        await updateProfile(userCredential.user, { displayName });
        // New users are not onboarded yet
        setIsOnboarded(false);
        return { isNewUser: true };
      } catch (error) {
        // If credential is already in use, sign out anonymous user and sign in with existing account
        if ((error as AuthError).code === "auth/credential-already-in-use") {
          console.debug("[AuthContext] Email already in use, signing in instead of registering");
          if (auth) await signOut(auth);
          if (!auth) throw new Error("Firebase auth not initialized");
          try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            await updateProfile(userCredential.user, { displayName });
            // Check onboarding status for existing user
            checkUserStatus(userCredential.user)
              .then((onboarded) => {
                if (
                  auth?.currentUser?.uid === userCredential.user.uid &&
                  !isBrowserSessionClearing()
                )
                  setIsOnboarded(onboarded);
              })
              .catch(() => setIsOnboarded(false));
            // This was NOT a new signup - credential already existed
            return { isNewUser: false };
          } catch (signInError) {
            throw toFriendlyAuthError(signInError, {
              fallback: "An account with this email already exists. Try signing in instead.",
              flow: "sign-up",
            });
          }
        } else {
          throw toFriendlyAuthError(error, {
            fallback: "We couldn't create your account. Please try again.",
            flow: "sign-up",
          });
        }
      }
    } else {
      // No anonymous user, create a new account
      if (!auth) throw new Error("Firebase auth not initialized");
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(userCredential.user, { displayName });
        // New users are not onboarded yet
        setIsOnboarded(false);
        return { isNewUser: true };
      } catch (error) {
        throw toFriendlyAuthError(error, {
          fallback: "We couldn't create your account. Please try again.",
          flow: "sign-up",
        });
      }
    }
  };

  const loginWithGoogle = async (): Promise<OAuthAuthResult> => {
    assertCurrentBrowserSession(undefined);
    console.debug("[AuthContext] ===== loginWithGoogle START =====");
    const provider = new GoogleAuthProvider();
    const currentUser = auth?.currentUser;
    const useRedirect = isProductionDomain();
    console.debug("[AuthContext] Current user:", {
      uid: currentUser?.uid,
      isAnonymous: currentUser?.isAnonymous,
      email: currentUser?.email,
    });
    console.debug("[AuthContext] Using redirect:", useRedirect);

    if (!auth) throw new Error("Firebase auth not initialized");

    try {
      if (useRedirect) {
        // Production domain: use redirect (works better on mobile, no popup issues)
        // Don't sign out anonymous user - Firebase handles this during redirect
        console.debug("[AuthContext] Using signInWithRedirect for production domain...");
        await signInWithRedirect(auth, provider);
        // Page will redirect, code below won't execute
        return { status: "redirect" };
      } else {
        // Preview/dev domain: use popup (redirect doesn't work with dynamic URLs)
        if (currentUser && currentUser.isAnonymous) {
          console.debug("[AuthContext] Signing out anonymous user before popup OAuth...");
          await signOut(auth);
          // Brief delay to allow Firebase auth state to settle after sign-out
          // This prevents a race condition where the popup opens before the
          // anonymous user is fully signed out, which can cause linking errors
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        console.debug("[AuthContext] Using signInWithPopup for non-production domain...");
        const userCredential = await signInWithPopup(auth, provider);
        const additionalInfo = getAdditionalUserInfo(userCredential);
        const isNewUser = additionalInfo?.isNewUser ?? false;
        console.debug("[AuthContext] signInWithPopup successful!", { isNewUser });

        // Update onboarding status without blocking
        checkUserStatus(userCredential.user)
          .then((onboarded) => {
            if (auth?.currentUser?.uid === userCredential.user.uid && !isBrowserSessionClearing())
              setIsOnboarded(onboarded);
          })
          .catch(() => setIsOnboarded(false));

        console.debug("[AuthContext] ===== loginWithGoogle END =====");
        return { status: "success", isNewUser };
      }
    } catch (error) {
      if (isPopupClosedAuthError(error)) {
        return { status: "cancelled" };
      }

      throw toFriendlyAuthError(error, {
        fallback: "We couldn't complete authentication. Please try again.",
        flow: "oauth",
      });
    }
  };

  const loginWithApple = async (): Promise<OAuthAuthResult> => {
    assertCurrentBrowserSession(undefined);
    console.debug("[AuthContext] ===== loginWithApple START =====");
    const provider = new OAuthProvider("apple.com");
    const currentUser = auth?.currentUser;
    const useRedirect = isProductionDomain();
    console.debug("[AuthContext] Current user:", {
      uid: currentUser?.uid,
      isAnonymous: currentUser?.isAnonymous,
      email: currentUser?.email,
    });
    console.debug("[AuthContext] Using redirect:", useRedirect);

    if (!auth) throw new Error("Firebase auth not initialized");

    try {
      if (useRedirect) {
        // Production domain: use redirect (works better on mobile, no popup issues)
        // Don't sign out anonymous user - Firebase handles this during redirect
        console.debug("[AuthContext] Using signInWithRedirect for Apple on production domain...");
        await signInWithRedirect(auth, provider);
        // Page will redirect, code below won't execute
        return { status: "redirect" };
      } else {
        // Preview/dev domain: use popup (redirect doesn't work with dynamic URLs)
        if (currentUser && currentUser.isAnonymous) {
          console.debug("[AuthContext] Signing out anonymous user before Apple popup OAuth...");
          await signOut(auth);
          // Brief delay to allow Firebase auth state to settle after sign-out
          // This prevents a race condition where the popup opens before the
          // anonymous user is fully signed out, which can cause linking errors
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        console.debug("[AuthContext] Using signInWithPopup for Apple on non-production domain...");
        const userCredential = await signInWithPopup(auth, provider);
        const additionalInfo = getAdditionalUserInfo(userCredential);
        const isNewUser = additionalInfo?.isNewUser ?? false;
        console.debug("[AuthContext] Apple signInWithPopup successful!", { isNewUser });

        // Update onboarding status without blocking
        checkUserStatus(userCredential.user)
          .then((onboarded) => {
            if (auth?.currentUser?.uid === userCredential.user.uid && !isBrowserSessionClearing())
              setIsOnboarded(onboarded);
          })
          .catch(() => setIsOnboarded(false));

        console.debug("[AuthContext] ===== loginWithApple END =====");
        return { status: "success", isNewUser };
      }
    } catch (error) {
      if (isPopupClosedAuthError(error)) {
        return { status: "cancelled" };
      }

      throw toFriendlyAuthError(error, {
        fallback: "We couldn't complete authentication. Please try again.",
        flow: "oauth",
      });
    }
  };

  const logout = async () => {
    console.debug("[AuthContext] Logging out...");
    if (!auth) return;

    // Clear the guest ID from localStorage so a new one is generated
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("cosimo_guest_user_id");
    }

    await signOut(auth);
    setIsOnboarded(false);
    // The auth state listener will automatically sign in anonymously
    console.debug("[AuthContext] Logged out, waiting for anonymous sign-in");
  };

  const updateUserProfile = async (displayName: string, photoURL?: string) => {
    if (!auth?.currentUser) throw new Error("No user logged in");
    await updateProfile(auth.currentUser, { displayName, photoURL });
  };

  const setOnboardingComplete = async () => {
    if (!auth?.currentUser) return;

    try {
      // Mark user as onboarded via API
      await updateUser({ is_onboarded: true });
      setIsOnboarded(true);
    } catch (error) {
      console.error("Error setting onboarding complete:", error);
      throw error;
    }
  };

  const refreshOnboardingStatus = async () => {
    if (!auth?.currentUser) return;

    try {
      const userData = await getUser();
      setIsOnboarded(userData.is_onboarded || false);
    } catch (error) {
      console.error("Error refreshing onboarding status:", error);
    }
  };

  const userId = user?.uid || null;
  const isAuthenticated = !!user && !user.isAnonymous;
  const isAnonymous = user?.isAnonymous || false;

  // Debug logging when auth state changes
  useEffect(() => {
    console.debug("[AuthContext] State updated:", {
      userId,
      isAuthenticated,
      isAnonymous,
      userExists: !!user,
      loading,
      isOnboarded,
    });
  }, [userId, isAuthenticated, isAnonymous, user, loading, isOnboarded]);

  // Legacy function for compatibility
  const checkAuthStatus = async () => {
    // Firebase handles this automatically
    return Promise.resolve();
  };

  // Ensure we always have a stable user identifier that is consistent across sessions.
  // If the user is authenticated, use their Firebase UID. Otherwise, fall back to a
  // guest identifier that we persist to localStorage so the same ID is reused even
  // after a page refresh. This guarantees that any conversations (threads) started
  // while not logged-in can still be retrieved later in the same browser session.
  const ensureUserId = (): string => {
    if (sessionUnavailable) return "pending-user";
    // Prefer the authenticated Firebase UID if available
    if (userId) {
      return userId;
    }

    // Generate or retrieve a persistent guest ID in the browser environment
    if (typeof window !== "undefined") {
      const LOCAL_STORAGE_KEY = "cosimo_guest_user_id";
      let guestId = window.localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!guestId) {
        guestId = uuidv4();
        window.localStorage.setItem(LOCAL_STORAGE_KEY, guestId);
      }
      return guestId;
    }

    // Fallback (should rarely be used because we should be on the client)
    return "pending-user";
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userId,
        isAuthenticated,
        isAnonymous,
        isOnboarded,
        loading,
        login,
        resetPassword,
        register,
        loginWithGoogle,
        loginWithApple,
        logout,
        updateUserProfile,
        setOnboardingComplete,
        refreshOnboardingStatus,
        // Legacy compatibility
        checkAuthStatus,
        ensureUserId,
      }}
    >
      {sessionCleared ? null : (
        <Fragment key={sessionUnavailable ? "unavailable" : "available"}>
          {sessionUnavailable ? (
            <div role="status">
              We couldn't restore your session. Public browsing is available. Reload to try again.
            </div>
          ) : null}
          {children}
        </Fragment>
      )}
    </AuthContext.Provider>
  );
};
